import clsx from 'clsx';
import React, { useEffect } from 'react';

import { Warning as Warn, CircleCheck as Check } from '@td-client-frontend/icons';
import { FieldInputProps } from 'formik';

interface Props {
  field?: FieldInputProps<any>;
  password?: string;
  verifyPassword?: string;
}

const oneCapTest = new RegExp(/[A-Z]+/);
const oneLowerTest = new RegExp(/[a-z]+/);
const oneSpecialTest = new RegExp(/[!"#$%&'()* +,./:;<=>?@^_`\\[{|}\]~-]+/);
const oneNumberTest = new RegExp(/[0-9]+/);

const ValidationIcon = ({ validation, validationType }: { validation: boolean; validationType: string }) => {
  return validation ? (
    <Check data-testid={`${validationType}-success`} className="inline-block m-1 fill-current" />
  ) : (
    <Warn data-testid={`${validationType}-fail`} className="inline-block m-1 fill-current" />
  );
};

const PasswordValidation = ({ field }: Props) => {
  const password = field?.value;
  const [oneCapital, setOneCapital] = React.useState(false);
  const [oneLower, setOneLower] = React.useState(false);
  const [oneSpecial, setOneSpecial] = React.useState(false);
  const [oneNumber, setOneNumber] = React.useState(false);
  const [minLength, setMinLength] = React.useState(false);

  useEffect(() => {
    setMinLength(!!(password && password.length > 9));
    setOneCapital(oneCapTest.test(password));
    setOneLower(oneLowerTest.test(password));
    setOneSpecial(oneSpecialTest.test(password));
    setOneNumber(oneNumberTest.test(password));
  }, [password, oneCapital, oneLower, oneSpecial, oneNumber, minLength]);

  const validateClassname = (valid: boolean) => clsx(valid && 'text-success', 'text-red-700', 'flex items-center py-1');

  return (
    <div className="flex flex-col" id={`${field?.name}_error`} aria-live="assertive" role="alert">
      <span data-testid="one-cap" className={validateClassname(oneCapital)}>
        <ValidationIcon validationType="one-cap" validation={oneCapital} />
        One capital letter
      </span>
      <span className={validateClassname(oneSpecial)}>
        <ValidationIcon validationType="one-special" validation={oneSpecial} />
        One special character (e.g. $#*)
      </span>
      <span className={validateClassname(oneLower)}>
        <ValidationIcon validationType="one-lower" validation={oneLower} />
        One lower case letter
      </span>
      <span className={validateClassname(minLength)}>
        <ValidationIcon validationType="min-length" validation={minLength} />
        At least 10 characters
      </span>
      <span className={validateClassname(oneNumber)}>
        <ValidationIcon validationType="one-number" validation={oneNumber} />
        One number
      </span>
    </div>
  );
};

export default PasswordValidation;
